import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Body1, Caption1, Spinner,
  MessageBar, MessageBarBody, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useWsjfBacklog } from '../../../api/hooks';

// FEAT-017 WSJF Backlog. Backlog Features + User Stories ranked by economic priority
// (BV + TC) / Story Points. Rows missing prioritisation are pushed to the bottom and
// tagged "Score Me" — the link is a placeholder that future work will deep-link to ADO.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  tableWrapper: { flex: 1, overflow: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  rank: { fontFamily: tokens.fontFamilyMonospace },
  score: { fontFamily: tokens.fontFamilyMonospace, fontWeight: tokens.fontWeightSemibold },
});

export function WsjfBacklogPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useWsjfBacklog(projectId);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>WSJF Backlog</Title2>
        <Body1 className={styles.hint}>
          Sequence work by economic priority. WSJF = (Business Value + Time Criticality) / Story Points.
          Rows missing prioritisation YAML sink to the bottom with a "Score Me" badge.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project to see its ranked backlog.</Body1>
      ) : isLoading ? (
        <Spinner label="Computing WSJF…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.ranked.length === 0 ? (
        <Body1>No active backlog items in this project.</Body1>
      ) : (
        <div className={styles.tableWrapper}>
          <Table aria-label="WSJF-ranked backlog" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>#</TableHeaderCell>
                <TableHeaderCell>Title</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>BV</TableHeaderCell>
                <TableHeaderCell>TC</TableHeaderCell>
                <TableHeaderCell>Effort (SP)</TableHeaderCell>
                <TableHeaderCell>WSJF</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.ranked.map((row, idx) => (
                <TableRow key={row.id}>
                  <TableCell className={styles.rank}>{idx + 1}</TableCell>
                  <TableCell>
                    <Body1>{row.title}</Body1>
                    {row.missingPrioritisation ? (
                      <>
                        {' '}<Badge appearance="outline" color="warning">Score Me</Badge>
                      </>
                    ) : null}
                  </TableCell>
                  <TableCell><Caption1>{row.workItemType}</Caption1></TableCell>
                  <TableCell className={styles.rank}>{row.businessValue.toFixed(1)}</TableCell>
                  <TableCell className={styles.rank}>{row.timeCriticality.toFixed(1)}</TableCell>
                  <TableCell className={styles.rank}>{row.storyPoints ?? '—'}</TableCell>
                  <TableCell className={styles.score}>{row.wsjfScore.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
